/**
 * Deal Flow API
 * Handles all deal flow script builder API calls
 */

import { BASE_URL, fetchWithAuth } from './client';

/**
 * Helper to parse response and handle errors
 */
const handleResponse = async (response, errorMessage = 'Request failed') => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || errorMessage);
    }
    return response.json();
};

/**
 * Fetch all deal flows
 */
export const fetchDealFlows = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `${BASE_URL}/api/claw9/deal-flows${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    return handleResponse(response, 'Failed to fetch deal flows');
};

/**
 * Fetch a single deal flow by ID
 */
export const fetchDealFlowById = async (flowId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}`);
    return handleResponse(response, 'Failed to fetch deal flow');
};

/**
 * Create a new deal flow
 */
export const createDealFlow = async (flowData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flowData),
    });
    return handleResponse(response, 'Failed to create deal flow');
};

/**
 * Update an existing deal flow
 */
export const updateDealFlow = async (flowId, flowData) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flowData),
    });
    return handleResponse(response, 'Failed to update deal flow');
};

/**
 * Delete a deal flow
 */
export const deleteDealFlow = async (flowId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}`, {
        method: 'DELETE',
    });
    return handleResponse(response, 'Failed to delete deal flow');
};

/**
 * Duplicate a deal flow
 */
export const duplicateDealFlow = async (flowId) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}/duplicate`, {
        method: 'POST',
    });
    return handleResponse(response, 'Failed to duplicate deal flow');
};

/**
 * Publish/unpublish a deal flow
 */
export const toggleDealFlowStatus = async (flowId, status) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
    });
    return handleResponse(response, 'Failed to update deal flow status');
};

/**
 * Get deal flow analytics
 */
export const getDealFlowAnalytics = async (flowId, dateRange) => {
    const params = new URLSearchParams();
    if (dateRange?.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange?.endDate) params.append('endDate', dateRange.endDate);

    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}/analytics?${params.toString()}`);
    return handleResponse(response, 'Failed to fetch deal flow analytics');
};

/**
 * Test a deal flow with sample input
 */
export const testDealFlow = async (flowId, testInput) => {
    const response = await fetchWithAuth(`${BASE_URL}/api/claw9/deal-flows/${flowId}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: testInput }),
    });
    return handleResponse(response, 'Failed to test deal flow');
};
